import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, List, ListItemButton, ListItemText, TextField, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';

import { Permission, usePermission } from '../features/authorization';

/** One module: a URL segment under /admin, and what the nav calls it. */
export interface AdminModule {
  slug: string;
  label: string;
  /**
   * Hides the entry from anybody without it. The RPCs behind a module refuse
   * the wrong operator anyway, so a link they cannot use would only lead to a
   * screen of errors.
   */
  permission?: Permission;
}

export interface AdminGroup {
  heading: string;
  items: AdminModule[];
}

/**
 * The Admin screen's own navigation.
 *
 * Admin was one column of accordions several screens tall: every module in
 * the document whether or not anybody was looking at it, and the thing being
 * worked on scrolling away from its own heading. Each module is now its own
 * address under /admin, which means only the open one is mounted — and, since
 * they are loaded on demand, only the open one is downloaded.
 *
 * Grouped rather than listed flat because the groups answer different
 * questions: what the catalogue holds, how journeys are shaped, who may do
 * any of it, and how the system itself is set up.
 *
 * A heading and its modules first looked alike — same size, same weight, same
 * indent — so the groups read as one long list with odd words in it. The two
 * are now told apart three ways at once: the heading is small, spaced and
 * quiet where the modules are not; the modules are indented under it against
 * a rule that shows how far the group reaches; and a line separates one group
 * from the next. Any one of those alone was too subtle to survive a glance.
 */
export function AdminNav({ groups, onNavigate }: {
  groups: AdminGroup[];
  /**
   * Which module was asked for, at the moment it was asked for — before the
   * router has changed address and before its code has arrived. The page uses
   * it to say that the click was heard, which nothing else is in a position
   * to know: by the time a route renders, the waiting is over.
   */
  onNavigate?: (slug: string) => void;
}) {
  /*
   * Read as plain booleans here rather than wrapping each entry in
   * <Authorized>: the list is filtered, not decorated, and a hook cannot be
   * called from inside a map whose length changes with permissions.
   */
  const canManageUsers = usePermission(Permission.USER_MANAGE);
  const canManageRoles = usePermission(Permission.ROLE_MANAGE);
  const canWriteSettings = usePermission(Permission.SETTINGS_WRITE);
  const allowed = (module: AdminModule) => {
    if (module.permission === Permission.USER_MANAGE) return canManageUsers;
    if (module.permission === Permission.ROLE_MANAGE) return canManageRoles;
    if (module.permission === Permission.SETTINGS_WRITE) return canWriteSettings;

    return true;
  };

  // Emptied by permissions, and dropped before anything is drawn: a divider
  // belongs between the groups that are shown, not the ones that were meant
  // to be.
  const visible = groups
    .map((group) => ({ ...group, items: group.items.filter(allowed) }))
    .filter((group) => group.items.length > 0);

  /*
   * On a phone the column becomes a single field.
   *
   * 208px of nav beside the module leaves about 130 for the module on a
   * 375px screen, which is not a narrow version of this page so much as an
   * unusable one. A dropdown costs one line, keeps the groups as subheadings,
   * and names the module you are on — which a horizontal strip of tabs, the
   * other obvious answer, does not do without scrolling to find the mark.
   */
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  if (compact) {
    const current = visible
      .flatMap((group) => group.items)
      .find((module) => location.pathname.endsWith(`/${module.slug}`));

    return (
      <Box component="nav" sx={{ mb: 1 }}>
        {/*
          Styled as the nav it is, not as a form field.

          A floating "Section" label and an empty outline read as something to
          fill in, on a screen where the only other outlined boxes are inputs.
          What this actually is, is the desktop nav's active entry with the
          rest folded behind it — so it borrows that entry's treatment: the
          same tint, the same rounding, the same weight on the name you are
          on. The chevron says the rest are still there.
        */}
        <TextField
          select
          size="small"
          fullWidth
          aria-label="Section"
          // Never an empty field: a module whose address matches no entry
          // still has to render something readable.
          value={current?.slug ?? ''}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              bgcolor: 'action.selected',
              fontWeight: 600,
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'divider' },
            },
            '& .MuiSelect-select': { py: 1.25 },
          }}
          onChange={(event) => {
            const slug = event.target.value;
            onNavigate?.(slug);
            void navigate(`/admin/${slug}`);
          }}
          slotProps={{
            /*
              The platform's own picker, not a rendered menu.

              MUI's menu opens on press and positions itself over the field,
              so the release that finished the tap landed on whatever item sat
              under the finger — the list appeared and chose for you in the
              same gesture. A native select has no such window: the browser
              takes the whole interaction and hands back a value.

              It is also the better control here. On a phone this becomes the
              OS wheel or sheet, which is bigger, scrollable with a thumb, and
              already familiar.
            */
            select: { native: true },
          }}
        >
          {visible.map((group) => (
            <optgroup key={group.heading} label={group.heading}>
              {group.items.map((module) => (
                <option key={module.slug} value={module.slug}>
                  {module.label}
                </option>
              ))}
            </optgroup>
          ))}
        </TextField>
      </Box>
    );
  }

  return (
    <Box
      component="nav"
      sx={{
        width: 208,
        flexShrink: 0,
        // Follows the page rather than scrolling off it: the nav is how you
        // leave a module, and a module can be taller than the window.
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
      }}
    >
      {/*
        Groups separated by space, not by lines.

        There were three border treatments inside a few pixels of each other:
        a rule between the groups, a second rule running down the side of each
        one, and a third on the active entry — which landed hard against the
        second, so a selected module read as a rounded tab with a square bar
        stuck to it. The horizontal rules were wider than the vertical ones
        they sat above, so none of it lined up either.

        A spaced, lettered heading already says where a group begins; drawing
        a line as well says it twice. What is left is the heading, the gap,
        and one mark for the entry you are on.
      */}
      {visible.map((group, index) => (
        <Box key={group.heading} sx={{ mt: index > 0 ? 2.5 : 0 }}>
          <Typography
            component="h2"
            sx={{
              px: 1,
              pb: 0.5,
              /*
                Bigger and darker than what it holds, which is the other half
                of the indent below.

                At 11px in the disabled grey it was the faintest thing in the
                column while being the thing that organises it — so the four
                groups read as decoration over one flat list. A parent should
                outweigh its children.
              */
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: 'text.primary',
              userSelect: 'none',
            }}
          >
            {group.heading}
          </Typography>

          <List dense disablePadding>
            {group.items.map((module) => (
              <ListItemButton
                key={module.slug}
                component={NavLink}
                /*
                 * Absolute, not relative to the module. These sit inside the
                 * `/admin/*` splat, where a bare "places" resolves against
                 * whatever is currently matched — so from /admin/cities it
                 * would aim at /admin/cities/places. Naming the address in
                 * full is the one form that means the same thing from every
                 * module.
                 */
                to={`/admin/${module.slug}`}
                // Said before the router acts on it: this is the only moment
                // anything knows a module has been asked for and is not yet
                // there.
                onClick={() => onNavigate?.(module.slug)}
                sx={{
                  py: 0.5,
                  /*
                    Stepped in from its heading, so the group reads as holding
                    these rather than merely sitting above them. Flush with it,
                    the heading looked like one more entry in a flat list that
                    happened to be shouting.
                  */
                  pl: 2.25,
                  pr: 1,
                  borderRadius: 1.5,
                  color: 'text.secondary',
                  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                  /*
                    Marked quietly, and in no colour of its own.

                    The bar along the top is where the accent lives, because
                    that is the question it answers — which part of the app
                    you are in. This one answers which page inside that part,
                    which is the smaller question, so it is said with a tint
                    and a little weight. Taking the accent here made the two
                    navs look like the same level of the same thing.
                  */
                  '&.active': {
                    bgcolor: 'action.selected',
                    color: 'text.primary',
                    '& .MuiListItemText-primary': { fontWeight: 600 },
                    '&:hover': { bgcolor: 'action.selected' },
                  },
                }}
              >
                <ListItemText
                  primary={module.label}
                  slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.6 } } }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
}
